// This is a part of the Cadcorp SIS source code. 
// Copyright (c) Cadcorp Limited 1991-2026 
import { Module } from 'Vuex';
import { Feature } from 'GeoJSON';

/* Abstractions */
import { IMapState, IRootState, IWatermark, ILocation, IInitialView } from '../../../../CommonScripts/Abstractions';

/* Modules */
import geognosisState from '../GeognoSIS/overlayState';

declare const ol: any;

const theState: IMapState = {
    olMap: null,
    currentLocation:null,
    initialMapState: null,
    baseLayers: null,
    currentBaseLayer: null,
    dataLayers: null,
    watermark: null,
    viewExtent: null,
    currentScale: null,
    mapTitle: null,
    copyright: null,
    resolutions: null,
    initialBaseLayer: null,
    initialDataLayers: null,
    attributions: [],
    initialLocation: null,
    currentFeature: null
};
const yearRegEx = /%year%/gmi;
const mapState: Module<IMapState, IRootState> = {
    namespaced: true,
    state: theState,
    mutations: {
        setBaseLayers(state, baseLayers) {
            state.baseLayers = baseLayers;
        },
        addBaseLayer(state, newBaseLayer) {
            if (!state.baseLayers)
                state.baseLayers = [newBaseLayer];
            else
                state.baseLayers.push(newBaseLayer);
        },
        setCurrentBaseLayer(state, currentBaseLayer) {
            state.currentBaseLayer = currentBaseLayer;
        },
        setCurrentLocation(state, currentLocation) {
            state.currentLocation = currentLocation;
        },
        setInitialMapState(state, initialMapState) {
            state.initialMapState = initialMapState;
        },
        setDataLayers(state, dataLayers) {
            state.dataLayers = dataLayers;
        },
        addDataLayers(state, dataLayers) {
            if (state.dataLayers === null) {
                state.dataLayers = dataLayers;
            } else
                state.dataLayers.push(...dataLayers);
        },
        removeDataLayer(state, dataLayer) {
            if (state.dataLayers !== null) {
                const index = state.dataLayers.indexOf(dataLayer);
                if (index !== -1) {
                    state.dataLayers.splice(index, 1);
                }
            }
        },
        setMap(state, map) {
            state.olMap = map;
        },
        setWatermark(state, watermark: IWatermark) {
            state.watermark = watermark;
        },
        setScale(state, scale: number) {
            state.currentScale = scale;
        },
        setMapTitle(state, title: string) {
            state.mapTitle = title;
        },
        setCopyright(state, copyright: string[]) {
            state.copyright = copyright;
        },
        setResolutions(state, resolutions: number[]) {
            state.resolutions = [...resolutions];
        },
        setInitialBaseLayer(state, baseLayer: string) {
            state.initialBaseLayer = baseLayer;
        },
        setInitialDataLayers(state, dataLayers: string[]) {
            state.initialDataLayers = [...dataLayers];
        },
        /** 
         * See map.vue where this is set. 
         * Attributions for all layers and Geognosis overlays; Bing and Open Street Maps have their own internal copyright attribution. 
         * Other layers have customisable copyrights.
         * Geognosis layers need attribute 'WML::Attribution$' for their copyrights to come through.
        */
        setAttributions(state, attributions: string[]): void {
            state.attributions = attributions;
        },
        setInitialLocation(state, location: IInitialView): void {
            state.initialLocation = { ...location };
        },
        setCurrentFeature(state, feature: Feature): void {
            state.currentFeature = feature;
        }
    },
    getters: {
        /** overall map copyright (settings page) will supersede any others */
        attribution: (state): string[] => { 
            return state.copyright && state.copyright.length > 0 ? state.copyright : state.attributions;
        },
        visibleDataLayers: (state): any[] => {
            let layers = null;
            if (state.dataLayers && state.dataLayers.length > 0)
                layers = state.dataLayers.filter((layer) => layer.getVisible());
            return layers;
        },
        currentScale: (state): number => {
            return state.currentScale;
        },
        getMapTitle: (state): string => {
            return state.mapTitle;
        },
        getResolutions: (state): number[] => {
            return state.resolutions;
        },
        visibleDataLayerNames: (state): string[] => {
            let layerNames: string[] = [];
            let layers: any[] = null;
            if (state.dataLayers && state.dataLayers.length > 0)
                layers = state.dataLayers.filter((layer) => {
                    return layer.getVisible() && !layer.get('isExtensionLayer') && !layer.get('userAdded');
                });
            if (layers && layers.length > 0) {
                layerNames = layers.map((l) => {
                    return l.get('name');
                });
            }
            return layerNames;
        },
        visibleBaseLayerName: (state): string => {
            let name = '';
            if (state.currentBaseLayer)
                name = state.currentBaseLayer.get('name');
            return name;
        },
        initialBaseLayer: (state): string => {
            return state.initialBaseLayer;
        },
        initialDataLayers: (state): string[] => {
            return state.initialDataLayers;
        },
        getWatermark: (state: IMapState): IWatermark => {
            return state.watermark;
        },
        getInitialLocation: (state: IMapState): IInitialView => {
            return state.initialLocation;
        },
        getCurrentFeature: (state: IMapState): Feature => {
            return state.currentFeature;
        }
    },
    actions: {
        setCurrentBaseLayer(context, layer) {
            if (context.state.baseLayers !== null) {
                for (var c = 0; c < context.state.baseLayers.length; c++) {
                    if (context.state.baseLayers[c] === layer) {
                        context.state.baseLayers[c].setVisible(true);
                    } else
                        context.state.baseLayers[c].setVisible(false);
                }
                context.commit('setCurrentBaseLayer', layer);
            }
        },
        removeDataLayer(context, layer) {
            const source = layer.getSource();
            let retVal = null;
            if (source.overlay) {
                retVal = source.overlay.remove()
                    .then(() => {
                        context.commit('removeDataLayer', layer);
                        context.commit('geognoSis/removeOverlay', source.overlay);
                    });
            }
            else {
                context.commit('removeDataLayer', layer);
                retVal = Promise.resolve(layer);
            }
            return retVal;
        },
        setCurrentFeature(context, data: { feature: Feature, featureLayer: any }) {
            let feature = null;
            const map = context.state.olMap;
            const view = map.getView();
            const format = new ol.format.GeoJSON({});
            if (data.featureLayer && data.featureLayer.getSource().overlay) { // a geognosis layer
                const overlay = data.featureLayer.getSource().overlay;
                // transform the data to the map crs if required
                const olfeature = format.readFeature(data.feature, {
                    dataProjection: `EPSG:${overlay.epsgCode}`,
                    featureProjection: view.getProjection()
                });
                // now get the get the transformed geojson object
                feature = format.writeFeatureObject(olfeature)
            }
            else
                feature = data.feature;
            context.commit('setCurrentFeature', feature);
        }
    },
    modules: {
        geognoSis: geognosisState
    }
}
export default mapState;

